import styled from '@emotion/styled';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { buildTree } from '../../../lib/buildTree.ts';
import { ToastManager } from '../../../ToastManager';

import { zonesStore } from '../../../store/zones/ZonesStore.ts';

import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';
import { useZonesTree } from '../../../store/zones/useZones.ts';

import { HomeyImages } from '../../../theme/HomeyImages';

import { Scroll } from '../../../components/common/Scroll';
import { ContentLoader } from '../../../components/content-loader/ContentLoader';
import { dragTypes } from '../../../components/dnd/dragTypes';

import { ZoneNavigationList } from './ZoneNavigationList';

export function ZoneNavigation(props) {
  const { api } = useApi();
  const zones = useZonesTree();
  const draftZone = props.draftZone;
  const setDraftZone = props.setDraftZone;
  const [editZoneId, setEditZoneId] = useState(null);

  const listToRender = useMemo(() => {
    if (zones.tree?.list == null) {
      return null;
    }

    const zonesList = zones.tree.list;

    // If the draft zone is in the list, return the list as is, otherwise add the draft zone to the list.
    if (draftZone == null || zonesList.some((item) => item.id === draftZone.id)) {
      return zonesList;
    }

    const newList = [...zonesList];

    // Recursive function to find the index of the last child of the parent or the parent itself.
    function getLastChildIndex(newList, parentId) {
      let parentIndex = newList.findIndex((item) => item.id === parentId);
      let lastChildIndex = parentIndex;

      newList.forEach((item, index) => {
        if (item.parent === parentId) {
          lastChildIndex = index;
        }
      });

      if (lastChildIndex === parentIndex) {
        return lastChildIndex;
      } else {
        return getLastChildIndex(newList, newList[lastChildIndex].id);
      }
    }

    const parent = newList.find((item) => item.id === draftZone.parentId);
    const lastChildIndex = getLastChildIndex(newList, draftZone.parentId);

    // Add the draft zone after the last child of the parent or the parent itself.
    newList.splice(lastChildIndex + 1, 0, {
      id: draftZone.id,
      level: parent.level + 1,
      parent: draftZone.parentId,
    });

    return newList;
  }, [zones.tree, draftZone]);

  // Remove draft zone if it was created successfully.
  useLayoutEffect(() => {
    if (draftZone && zones.tree?.list?.some((item) => item.id === draftZone.id)) {
      setDraftZone(null);
    }
  }, [zones.tree?.list, draftZone, setDraftZone]);

  const onDrop = useCallback(
    ({ type, dropId, dragId, position, targetParent }) => {
      if (type === dragTypes.ZONE) {
        const draggedZone = zones.byId[dragId];
        const targetZone = dropId ? zones.byId[dropId] : null;

        // If position is 'inside' (or undefined for backward compatibility), move zone inside target
        // API automatically places it at the end of the children
        if (!position || position === 'inside') {
          // Optimistic update
          const currentById = zonesStore.get().byId;
          zonesStore.set({
            byId: {
              ...currentById,
              [dragId]: {
                ...currentById[dragId],
                parent: dropId,
              },
            },
          });

          api.zones
            .updateZone({
              id: dragId,
              zone: {
                name: draggedZone.name,
                parent: dropId,
              },
            })
            .catch((error) => {
              console.error(error);
              // Fallback: restore store if error occurs
              zonesStore.set({ byId: currentById });
            });
        } else {
          // Reorder zones
          const parentId = targetParent || targetZone?.parent;
          const siblings = zones.tree.list
            .filter((item) => item.parent === parentId && item.id !== dragId)
            .map((item) => item.id);

          const newSiblingsOrder = [...siblings];
          const targetIndex = dropId ? newSiblingsOrder.indexOf(dropId) : -1;

          if (position === 'below' && targetIndex !== -1) {
            newSiblingsOrder.splice(targetIndex + 1, 0, dragId);
          } else if (position === 'above' && targetIndex !== -1) {
            newSiblingsOrder.splice(targetIndex, 0, dragId);
          } else {
            newSiblingsOrder.push(dragId);
          }

          // Optimistic update for reorder
          const nextById = { ...zonesStore.get().byId };

          newSiblingsOrder.forEach((id, index) => {
            nextById[id] = {
              ...nextById[id],
              sortIndex: index,
              parent: id === dragId ? parentId : nextById[id].parent,
            };
          });

          zonesStore.set({ byId: nextById });

          // Update sortIndex for all siblings
          const zonesSortIndexUpdate = newSiblingsOrder.reduce((acc, id, index) => {
            acc[id] = { sortIndex: index };

            // Update parent if changed
            if (id === dragId && draggedZone.parent !== parentId) {
              acc[id].parent = parentId;
            }

            return acc;
          }, {});

          api
            .call({
              method: 'PUT',
              path: '/api/manager/zones/zone',
              body: { zones: zonesSortIndexUpdate },
            })
            .catch((error) => {
              console.error(error);
              // Fallback: restore store if error occurs
              zonesStore.restore();
              ToastManager.handleError(error);
            });
        }
      }

      if (type === dragTypes.DEVICE) {
        api.devices
          .updateDevice({
            id: dragId,
            device: {
              zone: dropId,
            },
          })
          .then(() => {})
          .catch(console.error);
      }
    },
    [api, zones.byId, zones.tree],
  );

  function handleEditSave({ id, value, resolve, reject }) {
    api.zones
      .updateZone({ id, zone: { name: value } })
      .then(resolve)
      .catch((error) => {
        reject(error);
        ToastManager.handleError(error);
      });
  }

  // Create a new zone and update the draft zone with the new id as a placeholder.
  function handleCreateZone({ id, parentId, value }) {
    api.zones
      .createZone({
        id,
        zone: { name: value, parent: parentId },
      })
      .then((result) => {
        const state = zonesStore.store.getState();
        const tree = buildTree(state.byId);

        // If the store is updated before this resolves, clear the draft zone.
        if (tree.list?.some((item) => item.id === result.id)) {
          setDraftZone(null);
        } else {
          setDraftZone((prevDraftZone) => {
            return {
              ...prevDraftZone,
              id: result.id,
              name: value,
            };
          });
        }
      })
      .catch((error) => {
        ToastManager.handleError(error);
      });
  }

  return (
    <ZonesDrawer>
      {zones.isLoading && <ContentLoader.ZoneList />}

      {!zones.isLoading && !zones.error && (
        <>
          <Scroll>
            <ZoneNavigationList
              tree={zones.tree}
              listToRender={listToRender}
              byId={zones.byId}
              selectedId={props.selectedZoneId}
              draftZone={draftZone}
              editId={editZoneId}
              onSelect={({ id }) => props.setSelectedZoneId(id)}
              setDraftZone={setDraftZone}
              setEditZoneId={setEditZoneId}
              onDraftZoneBlur={() => {
                setDraftZone(null);
              }}
              onEditBlur={() => {
                setEditZoneId(null);
              }}
              onDraftZoneSave={handleCreateZone}
              onEditSave={handleEditSave}
              onDrop={onDrop}
              icon={getIcon}
            />
          </Scroll>
        </>
      )}
    </ZonesDrawer>
  );
}

function getIcon(item) {
  return HomeyImages.getIconForZone(item?.icon ?? null);
}

const ZonesDrawer = styled.aside`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  max-width: 100%;
`;
